/**
 * Free gift tiers: keeps the cart's gift lines in line with the tiers set up in
 * the mini-cart section (config output by snippets/free-gift-bar.liquid).
 *
 * - The highest tier the subtotal (gift lines excluded) has reached gets its gift.
 * - With the mystery gift on the last tier, every rule whose trigger product is in
 *   the cart (gift and gift-wrap lines ignored) adds its gift instead. If none
 *   applies, or none can be added, the tier's normal gift is used.
 * - Each wanted gift is kept once at quantity 1; every other gift line is removed.
 *
 * Gift lines carry the `_free_gift` property (the tier id). A Shopify automatic
 * discount makes them free: this script only adds and removes lines.
 *
 * It reacts to the theme's cart events, waits for the drawer's own delayed
 * re-render to land, then syncs from a freshly fetched cart and re-renders the
 * drawer through the theme's "cart:refresh" event. Syncs never overlap: a change
 * during a sync gets one more pass afterwards.
 */
(() => {
  const PROPERTY = "_free_gift";
  // The drawer re-renders up to 750ms after a quantity change with the HTML of
  // that moment. Waiting past it makes our re-render the last one to land.
  const SETTLE_DELAY = 800;
  // A gift that failed for another reason than being refused is retried later.
  const RETRY_AFTER = 30000;

  const failedAt = new Map(); // variant id -> time of the failed add (Infinity: refused)
  let timer = null;
  let running = false;
  let again = false;
  let dispatching = false;

  const isGift = (item) => Boolean(item.properties && item.properties[PROPERTY]);
  const giftId = (tier, variantId) => `${tier}|${variantId}`;
  const canAdd = (variantId) => !failedAt.has(variantId) || Date.now() - failedAt.get(variantId) > RETRY_AFTER;

  function readConfig() {
    const element = document.querySelector("script[data-free-gift-config]");
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      return null;
    }
  }

  async function request(url, body) {
    const response = await fetch(url, {
      method: body ? "POST" : "GET",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: body ? JSON.stringify(body) : undefined
    });
    if (!response.ok) {
      const error = new Error(`${url} failed: ${response.status}`);
      error.status = response.status;
      throw error;
    }
    return response.json();
  }

  function wantedGifts(config, cart) {
    const paidItems = cart.items.filter((item) => !isGift(item));
    const subtotal = paidItems.reduce((sum, item) => sum + item.final_line_price, 0);
    const tier = config.tiers
      .filter((t) => subtotal >= t.threshold)
      .sort((a, b) => b.threshold - a.threshold)[0];
    if (!tier) return [];

    const normalGift = [{ tier: tier.id, variantId: tier.variantId }];
    const mystery = config.mystery;
    if (!mystery || mystery.tier !== tier.id) return normalGift;

    const giftWrap = mystery.giftWrapHandle;
    const productsInCart = new Set(
      paidItems.filter((item) => !giftWrap || item.handle !== giftWrap).map((item) => item.product_id)
    );
    const gifts = [];
    for (const rule of mystery.rules) {
      if (!canAdd(rule.variantId) || gifts.some((gift) => gift.variantId === rule.variantId)) continue;
      if (rule.triggers.some((id) => productsInCart.has(id))) {
        gifts.push({ tier: tier.id, variantId: rule.variantId, mystery: true });
      }
    }
    return gifts.length > 0 ? gifts : normalGift;
  }

  /**
   * Loading state while gift lines change, built on the theme's own pieces: gift
   * lines being removed or reset get the <line-item> spinner and, once done, its
   * collapse animation (the same events a quantity change uses). A status line
   * covers gifts being added, and checkout is paused until the lines are final.
   *
   * @param {Object<string, number>} updates line key -> new quantity
   * @param {boolean} adding whether gifts will be added
   */
  function showBusy(updates, adding) {
    const drawer = document.getElementById("mini-cart");
    if (!drawer) return { linesUpdated() {}, end() {} };

    const lines = Object.keys(updates)
      .map((key) => [drawer.querySelector(`line-item[data-free-gift-key="${CSS.escape(key)}"]`), updates[key]])
      .filter(([element]) => element);
    lines.forEach(([element]) => element.dispatchEvent(new CustomEvent("line-item-quantity:change:start")));

    const status = drawer.querySelector("[data-free-gift-status]");
    if (status) {
      const removing = Object.keys(updates).length > 0;
      const text = adding && removing ? status.dataset.textUpdating : adding ? status.dataset.textAdding : status.dataset.textRemoving;
      status.querySelector("[data-free-gift-status-text]").textContent = text;
      status.hidden = false;
    }

    const footer = drawer.querySelector(".drawer__footer");
    if (footer) {
      footer.classList.add("drawer__footer--free-gift-busy");
      footer.setAttribute("aria-busy", "true");
    }

    let linesDone = false;
    return {
      linesUpdated(cart) {
        linesDone = true;
        lines.forEach(([element, quantity]) => element.dispatchEvent(
          new CustomEvent("line-item-quantity:change:end", { detail: { cart, newLineQuantity: quantity } })
        ));
      },
      end() {
        if (status) status.hidden = true;
        if (footer) {
          footer.classList.remove("drawer__footer--free-gift-busy");
          footer.removeAttribute("aria-busy");
        }
        // The update failed: just drop the spinners, the lines are unchanged.
        if (!linesDone) lines.forEach(([element]) => {
          const loader = element.querySelector(".line-item__loader");
          if (loader) loader.hidden = true;
        });
      }
    };
  }

  async function addGifts(gifts) {
    const url = `${window.themeVariables.routes.cartAddUrl}.js`;
    const line = (gift) => ({ id: gift.variantId, quantity: 1, properties: { [PROPERTY]: gift.tier } });
    const markFailed = (gift, error) => failedAt.set(gift.variantId, error.status === 422 ? Infinity : Date.now());

    try {
      return await request(url, { items: gifts.map(line), sections: ["mini-cart"] });
    } catch (error) {
      if (!gifts[0].mystery) return null;
      if (gifts.length === 1) {
        markFailed(gifts[0], error);
        return null;
      }
    }
    // Shopify rejects the whole request when one item fails, so add them one by one.
    let lastResponse = null;
    for (const gift of gifts) {
      try {
        lastResponse = await request(url, { items: [line(gift)], sections: ["mini-cart"] });
      } catch (error) {
        markFailed(gift, error);
      }
    }
    return lastResponse;
  }

  async function sync() {
    const config = readConfig();
    if (!config) return;

    const cartUrl = window.themeVariables.routes.cartUrl;
    const cart = await request(`${cartUrl}.js`);
    const wanted = wantedGifts(config, cart);
    const wantedIds = new Set(wanted.map((gift) => giftId(gift.tier, gift.variantId)));

    const updates = {};
    const kept = new Set();
    for (const item of cart.items.filter(isGift)) {
      const id = giftId(item.properties[PROPERTY], item.variant_id);
      if (wantedIds.has(id) && !kept.has(id)) {
        kept.add(id);
        if (item.quantity !== 1) updates[item.key] = 1;
      } else {
        updates[item.key] = 0;
      }
    }
    const missing = wanted.filter((gift) => !kept.has(giftId(gift.tier, gift.variantId)));
    if (Object.keys(updates).length === 0 && missing.length === 0) return;

    const busy = showBusy(updates, missing.length > 0);
    try {
      let lastResponse = null;
      if (Object.keys(updates).length > 0) {
        lastResponse = await request(`${cartUrl}/update.js`, { updates, sections: ["mini-cart"] });
        busy.linesUpdated(lastResponse);
      }
      if (missing.length > 0) {
        const addResponse = await addGifts(missing);
        if (addResponse) {
          lastResponse = addResponse;
        } else if (missing[0].mystery && kept.size === 0) {
          // No mystery gift could be added: the next pass falls back to the normal gift.
          again = true;
        }
      }
      if (!lastResponse) return;

      // Hand the result to the theme: cart count, drawer, etc.
      const freshCart = await request(`${cartUrl}.js`);
      freshCart.sections = lastResponse.sections;
      dispatching = true;
      try {
        document.documentElement.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true, detail: { cart: freshCart } }));
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true, detail: { cart: freshCart } }));
      } finally {
        dispatching = false;
      }
    } finally {
      busy.end();
    }
  }

  async function run() {
    if (running) {
      again = true;
      return;
    }
    running = true;
    try {
      do {
        again = false;
        try {
          await sync();
        } catch (error) {
          console.warn("Free gift sync failed", error);
        }
      } while (again);
    } finally {
      running = false;
    }
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(run, SETTLE_DELAY);
  }

  const onCartChange = () => {
    if (!dispatching) schedule();
  };
  document.documentElement.addEventListener("cart:updated", onCartChange);
  document.documentElement.addEventListener("cart:refresh", onCartChange);
  // Theme editor: gift settings changed.
  document.addEventListener("shopify:section:load", schedule);

  run();
})();
